import { AsYouType } from 'libphonenumber-js'
import { Flag } from 'primitives/icon'
import { Input } from 'primitives/input'
import { ScrollArea } from 'primitives/scroll-area'
import { Typography } from 'primitives/typography'
import type React from 'react'
import { type RefObject, useEffect, useRef, useState } from 'react'

interface CountryDropdownProps {
  visible: boolean
  setVisible: (visible: boolean) => void
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  textFieldRef: RefObject<HTMLInputElement>
  countryISO: React.MutableRefObject<string | null>
  value: string
}

const CountryDropdown: React.FC<CountryDropdownProps> = ({
  visible,
  setVisible,
  onChange,
  textFieldRef,
  countryISO,
  value,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const listHolderRef = useRef<HTMLDivElement>(null)

  const countriesList = window.contactoCountryData.filter((country) => {
    const countryName = country[0]?.toLowerCase()
    const countryCode = country[2].toLowerCase()
    const searchTermLower = searchTerm.toLowerCase().replace(/^\+/, '')
    return countryName?.includes(searchTermLower) || countryCode.includes(searchTermLower)
  })

  useEffect(() => {
    setSearchTerm('')
    setTimeout(() => {
      const position = listHolderRef.current?.querySelector('.selected')
      if (visible && position?.getBoundingClientRect().top) {
        listHolderRef.current?.scrollTo({ top: position.getBoundingClientRect().top - 150 })
      }
    }, 50)
  }, [visible])

  return (
    <div>
      <div className="bg-popover my-2">
        <Input
          type="search-box"
          onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
          value={searchTerm}
          placeholder="Search country"
        />
      </div>
      <ScrollArea>
        <div ref={listHolderRef} className="h-[220px] pr-3">
          {countriesList.length ? (
            countriesList.map((country) => (
              // biome-ignore lint/a11y/useSemanticElements: not using semantic elements
              <div
                key={`${country[1]}`} // Use a unique identifier
                className="p-1 flex items-center cursor-pointer hover:bg-hv1 gap-2"
                role="button"
                tabIndex={0}
                onClick={() => {
                  const asYouType = new AsYouType(country[1])
                  if (value) asYouType.input(value)
                  countryISO.current = country[1]
                  const event = new Event('input', { bubbles: true })
                  Object.defineProperty(event, 'target', {
                    writable: true,
                    value: {
                      value: `+${country[2]} ${asYouType.getNationalNumber()}`,
                    },
                  })
                  onChange(event as unknown as React.ChangeEvent<HTMLInputElement>)
                  setTimeout(() => {
                    textFieldRef?.current?.focus()
                  }, 100)
                  setVisible(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.currentTarget.click()
                  }
                }}
              >
                <Flag iso={country[1]} url="/flags.svg" circle size={20} />
                <Typography className="flex items-center justify-between flex-1" variant="P/400">
                  <span className="text-ellipsis max-w-[78%] overflow-auto">{country[0]}</span>
                  <span className="text-tertiary">+{country[2]}</span>
                </Typography>
              </div>
            ))
          ) : (
            <div className="text-s-500 h-11 flex items-center cursor-pointer px-4">
              No Countries found
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

export default CountryDropdown
